import { useEffect, useMemo, useRef, useState } from 'react';
import FJLocalStore from '../store/FJLocalStore';
import FJLongToShortStore from '../store/FJLongToShortStore';
import { PROCESS_TYPE } from '../constants/FJEnumConstant';

const PROGRESS_DISPLAY_JUMP_THRESHOLD = 3;
const PROGRESS_COUNT_UP_BUDGET_MS = 600;

/** 0–100 整数，用于进度文案与阈值判断 */
export function clampProgressPercent(n: number) {
    // return Math.max(0, Math.min(100, Number(n.toFixed(2))));
    return Math.max(0, Math.min(100, Math.round(n)));
}

/** ≥ 此进度后生成页改提示：需留在当前页完成后续处理（与人脸等流程对齐） */
export const GENERATING_PAGE_HINT_STAY_THRESHOLD =
    FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND).start * 100 +
    FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND).proportion * 100;

/**
 * 将上游进度平滑为「展示用」百分比：小幅跳变直接同步，大幅跳变则按步进动画，
 * 与生成页标题 `generating-percent` 的展示策略一致。
 */
export function useDisplayedProgressPercent(rawProgress: number): number {
    const clamped = clampProgressPercent(rawProgress);
    const shownRef = useRef(clamped);
    const [shownProgress, setShownProgress] = useState(() => clamped);

    useEffect(() => {
        const target = clampProgressPercent(rawProgress);
        const current = shownRef.current;

        if (target < current) {
            shownRef.current = target;
            queueMicrotask(() => setShownProgress(target));
            return;
        }

        const delta = target - current;
        if (delta <= PROGRESS_DISPLAY_JUMP_THRESHOLD) {
            shownRef.current = target;
            queueMicrotask(() => setShownProgress(target));
            return;
        }

        const stepMs = Math.max(18, Math.min(42, Math.floor(PROGRESS_COUNT_UP_BUDGET_MS / delta)));
        const id = window.setInterval(() => {
            const next = Math.min(target, shownRef.current + 1);
            shownRef.current = next;
            setShownProgress(next);
            if (next >= target) {
                window.clearInterval(id);
            }
        }, stepMs);

        return () => window.clearInterval(id);
    }, [rawProgress]);

    return shownProgress;
}

export type GeneratingProgressLabels = {
    /** 平滑后的展示百分比（0–100） */
    displayedPercent: number;
    /** 真实进度取整，用于阈值判断（如生成页底部提示） */
    clampedRaw: number;
    /** `generating-percent`，用于生成页标题 / 最近文件「生成中」遮罩 */
    generatingPercentLabel: string;
    /** `percent-complete-click-to-continue`，用于最近文件 pending 遮罩 */
    percentCompleteContinueLabel: string;
    /** 生成页底部提示文案键：高进度时建议留在页面 */
    generatingPageHintKey:
        | 'please-keep-this-page-open-to-complete-the-remaining-processing'
        | 'feel-free-to-leave-this-page-the-task-will-keep-running';
};

/**
 * 组合：平滑展示百分比 + 与进度相关的 i18n 文案（生成页、最近文件卡片共用）。
 */
export function useGeneratingProgressLabels(rawProgress: number): GeneratingProgressLabels {
    const displayedPercent = useDisplayedProgressPercent(rawProgress);
    const clampedRaw = clampProgressPercent(rawProgress);

    const generatingPercentLabel = useMemo(
        () => FJLocalStore._('generating-percent').replace(/\{percent\}/g, String(displayedPercent)),
        [displayedPercent],
    );

    const percentCompleteContinueLabel = useMemo(
        () => FJLocalStore._('percent-complete-click-to-continue').replace(/\{percent\}/g, String(displayedPercent)),
        [displayedPercent],
    );

    const generatingPageHintKey = useMemo<GeneratingProgressLabels['generatingPageHintKey']>(
        () =>
            clampedRaw >= GENERATING_PAGE_HINT_STAY_THRESHOLD
                ? 'please-keep-this-page-open-to-complete-the-remaining-processing'
                : 'feel-free-to-leave-this-page-the-task-will-keep-running',
        [clampedRaw],
    );

    return {
        displayedPercent,
        clampedRaw,
        generatingPercentLabel,
        percentCompleteContinueLabel,
        generatingPageHintKey,
    };
}
